import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { adminApi, indexnowApi } from '../lib/api';
import TrafficLineChart from '../components/TrafficLineChart';
import * as Sentry from '@sentry/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { 
  Users, FileText, BarChart3, Search, Eye, Ban, Trash2, 
  Loader2, Shield, ArrowLeft, UserX, RefreshCw, Settings,
  CreditCard, Mail, Activity, Bug, Key, Check, X, Save, Database, MessageSquare,
  TrendingUp, DollarSign, MousePointerClick, Globe, Newspaper, Pencil, Plus, ExternalLink
} from 'lucide-react';
import { formatDate, formatDateTime } from '../lib/utils';
import FFLoader from '../components/FFLoader';
// Test button for Sentry error tracking
const SentryTestButton = () => (
  <Button
    variant="destructive"
    size="sm"
    onClick={() => {
      try {
        throw new Error('This is a test error from Fantastic Forms!');
      } catch (error) {
        Sentry.captureException(error);
        toast.success('Test error sent to Sentry!');
      }
    }}
  >
    <Bug className="w-4 h-4 mr-2" />
    Test Sentry Error
  </Button>
);

/**
 * Native Traffic & Revenue dashboard. Replaces what we'd get from
 * Plausible — answers "where is traffic coming from?" + "what sources
 * generate the most revenue?" using our existing Supabase pageviews
 * + submissions data.
 */
const TrafficRevenuePanel = () => {
  const [days, setDays] = React.useState(30);
  const [loading, setLoading] = React.useState(true);
  const [data, setData] = React.useState(null);

  React.useEffect(() => {
    let cancelled = false;
    setLoading(true);
    adminApi.getTrafficRevenue(days)
      .then((res) => { if (!cancelled) setData(res); })
      .catch(() => { if (!cancelled) toast.error('Failed to load traffic data'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [days]);

  const k = data?.kpis || {};
  const sources = data?.sources || [];
  const pages = data?.pages || [];
  const keywords = data?.keywords || [];
  const utmCampaigns = data?.utm_campaigns || [];

  const usd = (n) => `$${(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const num = (n) => (n || 0).toLocaleString('en-US');

  return (
    <div className="space-y-6">
      {/* Date-range selector */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Traffic & Revenue</h3>
          <p className="text-xs text-zinc-500">
            Pro upgrades ($99 plan) attributed to acquisition channels. Admin browsing is excluded automatically.
          </p>
        </div>
        <div className="flex gap-1" data-testid="traffic-range-tabs">
          {[7, 30, 90, 365].map((d) => (
            <Button
              key={d}
              variant={days === d ? 'default' : 'outline'}
              size="sm"
              onClick={() => setDays(d)}
              data-testid={`traffic-range-${d}`}
            >
              {d === 365 ? '12mo' : `${d}d`}
            </Button>
          ))}
        </div>
      </div>

      {loading && !data ? (
        <div className="py-12 flex items-center justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-zinc-400" />
        </div>
      ) : (
        <>
          {/* KPI cards */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="traffic-kpis">
            <KpiCard icon={<Eye className="w-4 h-4" />}              label="Pageviews"        value={num(k.total_pageviews)} />
            <KpiCard icon={<Globe className="w-4 h-4" />}            label="Unique sessions"  value={num(k.unique_sessions)} />
            <KpiCard icon={<MousePointerClick className="w-4 h-4" />} label="Pro accounts"     value={num(k.total_conversions)} />
            <KpiCard icon={<TrendingUp className="w-4 h-4" />}       label="CVR (Pro/session)" value={`${(k.conversion_rate || 0).toFixed(2)}%`} />
            <KpiCard icon={<DollarSign className="w-4 h-4" />}       label="Revenue"          value={usd(k.total_revenue)} />
            <KpiCard icon={<DollarSign className="w-4 h-4" />}       label="ARPU"             value={usd(k.arpu)} />
          </div>

          {/* Time-series line chart — sits right under the KPI cards so
              admins can correlate today's numbers with the trend at a
              glance, like every other analytics dashboard (GA4, Plausible,
              Fathom). Two y-axes: traffic on the left, revenue on the
              right. Hover anywhere to snap to a day's full breakdown. */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Trend over time</CardTitle>
              <CardDescription>
                Daily pageviews, sessions, Pro upgrades, and revenue across the selected window. Click a metric chip to hide it.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <TrafficLineChart series={data?.series || []} />
            </CardContent>
          </Card>

          {/* Sources */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Top sources</CardTitle>
              <CardDescription>
                Acquisition channels — visits (pageview-side) and Pro upgrades attributed via first-touch UTM / referrer captured at signup.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Source</TableHead>
                    <TableHead className="text-right">Visits</TableHead>
                    <TableHead className="text-right">Sessions</TableHead>
                    <TableHead className="text-right">Pro</TableHead>
                    <TableHead className="text-right">CVR</TableHead>
                    <TableHead className="text-right">Revenue</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sources.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center text-sm text-zinc-500 py-8">
                        No traffic in this window yet — pageviews will appear once the migration is applied and visitors start landing on the marketing site.
                      </TableCell>
                    </TableRow>
                  ) : sources.map((row) => (
                    <TableRow key={row.source} data-testid={`source-row-${row.source}`}>
                      <TableCell className="font-medium">{row.source}</TableCell>
                      <TableCell className="text-right tabular-nums">{num(row.visits)}</TableCell>
                      <TableCell className="text-right tabular-nums text-zinc-500">{num(row.unique_sessions)}</TableCell>
                      <TableCell className="text-right tabular-nums">{num(row.conversions)}</TableCell>
                      <TableCell className="text-right tabular-nums">{(row.conversion_rate || 0).toFixed(2)}%</TableCell>
                      <TableCell className="text-right tabular-nums font-semibold">{usd(row.revenue)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {/* UTM-tagged campaigns — only counts traffic that arrived with
              an explicit `utm_source`. Surfaces the impact of branded
              back-links, partner campaigns, and paid ads in one table. */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">UTM campaigns</CardTitle>
              <CardDescription>
                Traffic and conversions from links tagged with <code className="px-1 py-0.5 rounded bg-zinc-100 text-zinc-700 text-[11px]">utm_source</code>.
                The branded &ldquo;made with Fantastic Forms&rdquo; link on free-plan forms is tagged <code className="px-1 py-0.5 rounded bg-zinc-100 text-zinc-700 text-[11px]">user / referral / branded</code>.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Source</TableHead>
                    <TableHead>Medium</TableHead>
                    <TableHead>Campaign</TableHead>
                    <TableHead className="text-right">Visits</TableHead>
                    <TableHead className="text-right">Sessions</TableHead>
                    <TableHead className="text-right">Pro</TableHead>
                    <TableHead className="text-right">CVR</TableHead>
                    <TableHead className="text-right">Revenue</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {utmCampaigns.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center text-sm text-zinc-500 py-8">
                        No UTM-tagged traffic in this window yet. Branded back-links and paid campaigns will appear here as soon as visitors click through.
                      </TableCell>
                    </TableRow>
                  ) : utmCampaigns.map((row, i) => {
                    const key = `${row.source}|${row.medium || ''}|${row.campaign || ''}`;
                    return (
                      <TableRow key={key} data-testid={`utm-row-${i}`}>
                        <TableCell className="font-medium">{row.source}</TableCell>
                        <TableCell className="text-zinc-500 text-sm">{row.medium || <span className="text-zinc-300">—</span>}</TableCell>
                        <TableCell className="text-zinc-500 text-sm">{row.campaign || <span className="text-zinc-300">—</span>}</TableCell>
                        <TableCell className="text-right tabular-nums">{num(row.visits)}</TableCell>
                        <TableCell className="text-right tabular-nums text-zinc-500">{num(row.unique_sessions)}</TableCell>
                        <TableCell className="text-right tabular-nums">{num(row.conversions)}</TableCell>
                        <TableCell className="text-right tabular-nums">{(row.conversion_rate || 0).toFixed(2)}%</TableCell>
                        <TableCell className="text-right tabular-nums font-semibold">{usd(row.revenue)}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {/* Keywords */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Top keywords</CardTitle>
              <CardDescription>
                Search terms driving traffic — `utm_term` (paid ads) plus extracted `q=` from search-engine referrers. Modern Google strips the query for organic results, so opaque visits bucket as "(not provided)".
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Keyword</TableHead>
                    <TableHead className="text-right">Visits</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {keywords.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={2} className="text-center text-sm text-zinc-500 py-8">
                        No keyword data yet. Paid campaigns should pass `utm_term=…` in their landing URL; organic search keywords appear when the referring engine forwards them.
                      </TableCell>
                    </TableRow>
                  ) : keywords.map((row) => (
                    <TableRow key={row.keyword} data-testid={`keyword-row-${encodeURIComponent(row.keyword)}`}>
                      <TableCell className="font-medium">{row.keyword}</TableCell>
                      <TableCell className="text-right tabular-nums">{num(row.visits)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {/* Pages */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Top pages</CardTitle>
              <CardDescription>
                Most-visited paths on the marketing surface.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Path</TableHead>
                    <TableHead className="text-right">Visits</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pages.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={2} className="text-center text-sm text-zinc-500 py-8">
                        No pageviews logged yet.
                      </TableCell>
                    </TableRow>
                  ) : pages.map((row) => (
                    <TableRow key={row.path} data-testid={`page-row-${encodeURIComponent(row.path)}`}>
                      <TableCell className="font-mono text-xs">{row.path}</TableCell>
                      <TableCell className="text-right tabular-nums">{num(row.visits)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

const KpiCard = ({ icon, label, value }) => (
  <Card>
    <CardContent className="p-4">
      <div className="flex items-center gap-2 text-xs text-zinc-500 mb-1">
        {icon}
        <span>{label}</span>
      </div>
      <div className="text-xl font-bold text-zinc-900 tabular-nums">{value}</div>
    </CardContent>
  </Card>
);


/**
 * Blog management panel — list / create / edit / publish-toggle / delete
 * blog posts directly from the admin dashboard. Posts created here are
 * stored with `source: "manual"` so they sit alongside RankLoop-ingested
 * articles and surface on the public `/blog` index once published.
 *
 * Editor is a single-column form with title/slug/meta_description/
 * featured-image-URL/content (markdown) and a status select. Slug auto-
 * derives from the title on first edit so the typical happy path is
 * "type title → write markdown → publish".
 */
const EMPTY_DRAFT = {
  title: '',
  slug: '',
  meta_description: '',
  featured_image_url: '',
  content: '',
  status: 'draft',
};

const slugifyClient = (text) => {
  if (!text) return '';
  return String(text).toLowerCase()
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_-]+/g, '-')
    .replace(/^-+|-+$/g, '');
};

const BlogStatusBadge = ({ status }) => {
  const map = {
    published: { label: 'Published', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
    draft:     { label: 'Draft',     cls: 'bg-amber-50 text-amber-700 border-amber-200' },
    archived:  { label: 'Archived',  cls: 'bg-zinc-100 text-zinc-600 border-zinc-200' },
  };
  const s = map[status] || map.draft;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium border ${s.cls}`}>
      {s.label}
    </span>
  );
};

const BlogManagementPanel = () => {
  const [loading, setLoading] = React.useState(true);
  const [posts, setPosts] = React.useState([]);
  const [tableError, setTableError] = React.useState(null);
  const [editorOpen, setEditorOpen] = React.useState(false);
  const [editing, setEditing] = React.useState(null);  // null = creating, object = editing
  const [draft, setDraft] = React.useState(EMPTY_DRAFT);
  const [saving, setSaving] = React.useState(false);
  const [slugTouched, setSlugTouched] = React.useState(false);
  const [confirmDelete, setConfirmDelete] = React.useState(null);
  const [deleting, setDeleting] = React.useState(false);

  const refresh = React.useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminApi.listBlogPosts();
      setPosts(res.posts || []);
      setTableError(res.warning || null);
    } catch (e) {
      setTableError(e?.response?.data?.detail || 'Failed to load posts');
      setPosts([]);
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => { refresh(); }, [refresh]);

  const openCreate = () => {
    setEditing(null);
    setDraft(EMPTY_DRAFT);
    setSlugTouched(false);
    setEditorOpen(true);
  };

  const openEdit = (post) => {
    setEditing(post);
    setDraft({
      title: post.title || '',
      slug: post.slug || '',
      meta_description: post.meta_description || '',
      featured_image_url: post.featured_image_url || '',
      content: post.content || '',
      status: post.status || 'draft',
    });
    setSlugTouched(true);  // existing slug shouldn't auto-track title
    setEditorOpen(true);
  };

  const onTitleChange = (val) => {
    setDraft((d) => ({
      ...d,
      title: val,
      slug: slugTouched ? d.slug : slugifyClient(val),
    }));
  };

  const onSlugChange = (val) => {
    setSlugTouched(true);
    setDraft((d) => ({ ...d, slug: slugifyClient(val) }));
  };

  const handleSave = async () => {
    if (!draft.title.trim()) { toast.error('Title is required'); return; }
    if (!draft.content.trim()) { toast.error('Content is required'); return; }

    const payload = {
      title: draft.title.trim(),
      slug: draft.slug || slugifyClient(draft.title),
      meta_description: draft.meta_description || null,
      featured_image_url: draft.featured_image_url || null,
      content: draft.content,
      status: draft.status,
    };

    setSaving(true);
    try {
      if (editing) {
        await adminApi.updateBlogPost(editing.id, payload);
        toast.success('Post updated');
      } else {
        await adminApi.createBlogPost(payload);
        toast.success('Post created');
      }
      setEditorOpen(false);
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save post');
    } finally {
      setSaving(false);
    }
  };

  const togglePublish = async (post) => {
    const nextStatus = post.status === 'published' ? 'draft' : 'published';
    try {
      await adminApi.updateBlogPost(post.id, { status: nextStatus });
      toast.success(nextStatus === 'published' ? 'Published' : 'Unpublished');
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to update status');
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    setDeleting(true);
    try {
      await adminApi.deleteBlogPost(confirmDelete.id);
      toast.success('Post deleted');
      setConfirmDelete(null);
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to delete post');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="blog-admin-panel">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <Newspaper className="w-5 h-5 text-[#FF6B6B]" />
            Blog Posts
          </h3>
          <p className="text-xs text-zinc-500">
            Manually-authored posts sit alongside RankLoop articles on the public <code className="text-[11px] bg-zinc-100 px-1 rounded">/blog</code> index.
          </p>
        </div>
        <Button onClick={openCreate} data-testid="blog-new-post-btn" className="bg-[#FF6B6B] hover:bg-[#fe5050] text-white">
          <Plus className="w-4 h-4 mr-2" />
          New post
        </Button>
      </div>

      {tableError && (
        <div className="border border-amber-200 bg-amber-50 text-amber-900 text-sm rounded-md px-3 py-2">
          {tableError}
        </div>
      )}

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="py-12 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-zinc-400" /></div>
          ) : posts.length === 0 ? (
            <div className="py-16 text-center text-zinc-500 text-sm" data-testid="blog-empty-state">
              No posts yet. Click <strong>New post</strong> to write your first one.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Title</TableHead>
                  <TableHead className="w-[110px]">Status</TableHead>
                  <TableHead className="w-[100px]">Source</TableHead>
                  <TableHead className="w-[150px]">Updated</TableHead>
                  <TableHead className="w-[260px] text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {posts.map((p) => (
                  <TableRow key={p.id} data-testid={`blog-row-${p.id}`}>
                    <TableCell>
                      <div className="font-medium text-zinc-900 leading-tight">{p.title}</div>
                      <div className="text-xs text-zinc-400 truncate max-w-md">/{p.slug}</div>
                    </TableCell>
                    <TableCell><BlogStatusBadge status={p.status} /></TableCell>
                    <TableCell>
                      <span className="text-xs uppercase tracking-wide text-zinc-500">{p.source || 'manual'}</span>
                    </TableCell>
                    <TableCell className="text-xs text-zinc-500">
                      {p.updated_at ? formatDate(p.updated_at) : '—'}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        {p.status === 'published' && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => window.open(`/blog/${p.slug}`, '_blank', 'noopener')}
                            title="View live"
                            data-testid={`blog-view-${p.id}`}
                          >
                            <ExternalLink className="w-3.5 h-3.5" />
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => togglePublish(p)}
                          title={p.status === 'published' ? 'Unpublish' : 'Publish'}
                          data-testid={`blog-toggle-${p.id}`}
                        >
                          {p.status === 'published' ? <X className="w-3.5 h-3.5" /> : <Check className="w-3.5 h-3.5" />}
                          <span className="ml-1 text-xs">{p.status === 'published' ? 'Unpublish' : 'Publish'}</span>
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEdit(p)}
                          title="Edit"
                          data-testid={`blog-edit-${p.id}`}
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setConfirmDelete(p)}
                          title="Delete"
                          data-testid={`blog-delete-${p.id}`}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Editor dialog */}
      <Dialog open={editorOpen} onOpenChange={(o) => { if (!saving) setEditorOpen(o); }}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="blog-editor-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit post' : 'New post'}</DialogTitle>
            <DialogDescription>
              Content is markdown — same renderer the public blog uses.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {/* Row 1: Title + Slug */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="blog-title">Title</Label>
                <Input
                  id="blog-title"
                  value={draft.title}
                  onChange={(e) => onTitleChange(e.target.value)}
                  placeholder="How we built our cascading-dropdown editor"
                  data-testid="blog-input-title"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="blog-slug">Slug</Label>
                <Input
                  id="blog-slug"
                  value={draft.slug}
                  onChange={(e) => onSlugChange(e.target.value)}
                  placeholder="how-we-built-cascading-dropdowns"
                  data-testid="blog-input-slug"
                />
                <p className="text-[11px] text-zinc-400">
                  Public URL: <code className="bg-zinc-100 px-1 rounded">/blog/{draft.slug || 'your-slug'}</code>
                </p>
              </div>
            </div>

            {/* Row 2: Meta description + Featured image URL */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="blog-meta">Meta description</Label>
                <Textarea
                  id="blog-meta"
                  rows={2}
                  value={draft.meta_description}
                  onChange={(e) => setDraft((d) => ({ ...d, meta_description: e.target.value }))}
                  placeholder="One-sentence summary shown on the blog index and in search results."
                  data-testid="blog-input-meta"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="blog-image">Featured image URL</Label>
                <Input
                  id="blog-image"
                  value={draft.featured_image_url}
                  onChange={(e) => setDraft((d) => ({ ...d, featured_image_url: e.target.value }))}
                  placeholder="https://…/cover.jpg"
                  data-testid="blog-input-image"
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="blog-content">Content (Markdown)</Label>
              <Textarea
                id="blog-content"
                rows={20}
                value={draft.content}
                onChange={(e) => setDraft((d) => ({ ...d, content: e.target.value }))}
                placeholder={"# Heading\n\nWrite your post here…"}
                className="font-mono text-sm"
                data-testid="blog-input-content"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Status</Label>
              <Select value={draft.status} onValueChange={(v) => setDraft((d) => ({ ...d, status: v }))}>
                <SelectTrigger data-testid="blog-input-status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">Draft (hidden from /blog)</SelectItem>
                  <SelectItem value="published">Published (live on /blog)</SelectItem>
                  <SelectItem value="archived">Archived (hidden)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setEditorOpen(false)} disabled={saving} data-testid="blog-cancel">
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving} className="bg-[#FF6B6B] hover:bg-[#fe5050] text-white" data-testid="blog-save">
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              <Save className="w-4 h-4 mr-2" />
              {editing ? 'Save changes' : 'Create post'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <Dialog open={!!confirmDelete} onOpenChange={(o) => { if (!o && !deleting) setConfirmDelete(null); }}>
        <DialogContent data-testid="blog-delete-dialog">
          <DialogHeader>
            <DialogTitle>Delete this post?</DialogTitle>
            <DialogDescription>
              <strong>{confirmDelete?.title}</strong> will be permanently removed. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setConfirmDelete(null)} disabled={deleting}>
              Cancel
            </Button>
            <Button onClick={handleDelete} disabled={deleting} className="bg-red-600 hover:bg-red-700 text-white" data-testid="blog-delete-confirm">
              {deleting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Yes, delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};



const Admin = () => {  const { user, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetails, setUserDetails] = useState(null);
  
  // Settings state
  const [platformSettings, setPlatformSettings] = useState(null);
  const [integrationStatus, setIntegrationStatus] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);

  // IndexNow bulk-submit state (SEO utilities card on the overview tab).
  const [indexnowBusy, setIndexnowBusy] = useState(false);

  const pushSitemapToIndexNow = async () => {
    setIndexnowBusy(true);
    try {
      const res = await indexnowApi.submitSitemap();
      const total = res?.total_urls ?? 0;
      const allOk = Array.isArray(res?.results) && res.results.every((r) => r.ok);
      if (allOk) {
        toast.success(`Submitted ${total} URLs to Bing IndexNow.`);
      } else {
        // Batch(es) returned a non-2xx but the call itself succeeded.
        // The most common first-time error is "SiteVerificationNotCompleted"
        // (HTTP 403) which just means Bing hasn't finished verifying the
        // /{key}.txt file yet. It's not fatal — the URLs are queued and
        // will process once verification lands (usually within a few
        // hours of the first submission). Surface a friendlier message
        // for that case; other 4xx/5xx responses fall through to the
        // generic warning.
        const bad = (res?.results || []).find((r) => !r.ok);
        const body = bad?.body || '';
        if (body.includes('SiteVerificationNotCompleted')) {
          toast.info(
            `IndexNow queued ${total} URLs but Bing is still verifying the site key. This is normal on the first submission and typically clears within a few hours (up to 24h). Retry then.`,
            { duration: 12000 },
          );
        } else {
          toast.warning(
            `IndexNow accepted ${total} URLs but returned ${bad?.status_code}. Response: ${body || '(empty)'}`,
          );
        }
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || 'IndexNow submission failed');
    } finally {
      setIndexnowBusy(false);
    }
  };

  // Credentials state
  const [credentials, setCredentials] = useState({
    stripe_secret_key: '',
    stripe_publishable_key: '',
    resend_api_key: '',
    supabase_url: '',
    supabase_service_key: '',
    feedback_board_url: ''
  });
  
  // Code injection state
  const [codeInjection, setCodeInjection] = useState({
    head_code: '',
    body_code: '',
    footer_code: ''
  });
  
  // SEO settings state
  const [seoSettings, setSeoSettings] = useState({
    site_title: 'Fantastic Forms - Build Beautiful Forms',
    site_description: 'Create stunning forms with conditional logic, file uploads, voice recordings, and more. Free form builder with premium features.',
    title_template: '%s | Fantastic Forms',
    meta_keywords: 'form builder, survey, typeform alternative, tally alternative',
    og_image_url: '',
    twitter_handle: '',
    robots_txt: `User-agent: *
Allow: /
Disallow: /api/
Disallow: /admin/
Sitemap: https://fantasticforms.com/sitemap.xml`
  });
  
  // Plans state
  const [plans, setPlans] = useState({
    free: {
      name: 'Free',
      price: '$0',
      features: ['Unlimited forms', 'Unlimited responses', '1MB file uploads', 'Basic analytics']
    },
    pro: {
      name: 'Pro',
      price: '$99',
      billing: 'Monthly',
      file_limit: '5MB',
      features: ['Voice recordings', 'Remove branding', 'Custom roles', 'Priority support', 'Advanced analytics']
    }
  });

  useEffect(() => {
    if (!isAdmin) {
      navigate('/dashboard');
      return;
    }
    fetchData();
  }, [isAdmin, navigate]);

  const fetchData = async () => {
    try {
      const [statsData, usersData, settingsData, integrationData, credentialsData] = await Promise.all([
        adminApi.getStats(),
        adminApi.getUsers(),
        adminApi.getSettings().catch(() => null),
        adminApi.getIntegrationStatus().catch(() => null),
        adminApi.getCredentials().catch(() => null)
      ]);
      setStats(statsData);
      setUsers(usersData.users);
      setPlatformSettings(settingsData);
      setIntegrationStatus(integrationData);
      
      // Pre-fill credentials from backend
      if (credentialsData) {
        setCredentials({
          stripe_secret_key: credentialsData.stripe_secret_key || '',
          stripe_publishable_key: credentialsData.stripe_publishable_key || '',
          resend_api_key: credentialsData.resend_api_key || '',
          supabase_url: credentialsData.supabase_url || '',
          supabase_anon_key: credentialsData.supabase_anon_key || '',
          supabase_publishable_key: credentialsData.supabase_publishable_key || '',
          supabase_secret_key: credentialsData.supabase_secret_key || '',
          feedback_board_url: credentialsData.feedback_board_url || ''
        });
      }
      
      // Pre-fill SEO, Code Injection, and Plans from settings
      if (settingsData) {
        if (settingsData.seo) {
          setSeoSettings(prev => ({
            ...prev,
            ...settingsData.seo
          }));
        }
        if (settingsData.code_injection) {
          setCodeInjection(prev => ({
            ...prev,
            ...settingsData.code_injection
          }));
        }
        if (settingsData.plans) {
          // Completely replace plans with server data, merging with defaults
          setPlans({
            free: {
              name: settingsData.plans.free?.name || 'Free',
              price: settingsData.plans.free?.price || '$0',
              features: settingsData.plans.free?.features || ['Unlimited forms', 'Unlimited responses', '1MB file uploads', 'Basic analytics']
            },
            pro: {
              name: settingsData.plans.pro?.name || 'Pro',
              price: settingsData.plans.pro?.price || '$99',
              billing: settingsData.plans.pro?.billing || 'Monthly',
              file_limit: settingsData.plans.pro?.file_limit || '5MB',
              features: settingsData.plans.pro?.features || ['Voice recordings', 'Remove branding', 'Custom roles', 'Priority support', 'Advanced analytics']
            }
          });
        }
      }
    } catch (error) {
      toast.error('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  const handleViewUser = async (userId) => {
    try {
      const data = await adminApi.getUser(userId);
      setUserDetails(data);
      setSelectedUser(userId);
    } catch (error) {
      toast.error('Failed to load user details');
    }
  };

  const handleBanUser = async (userId) => {
    if (!window.confirm('Are you sure you want to ban this user?')) return;
    
    try {
      await adminApi.banUser(userId);
      setUsers(prev => prev.map(u => 
        u.id === userId ? { ...u, status: 'banned' } : u
      ));
      toast.success('User banned');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to ban user');
    }
  };

  const handleUnbanUser = async (userId) => {
    try {
      await adminApi.unbanUser(userId);
      setUsers(prev => prev.map(u => 
        u.id === userId ? { ...u, status: 'active' } : u
      ));
      toast.success('User unbanned');
    } catch (error) {
      toast.error('Failed to unban user');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user? All their data will be permanently removed.')) return;
    
    try {
      await adminApi.deleteUser(userId);
      setUsers(prev => prev.filter(u => u.id !== userId));
      setSelectedUser(null);
      toast.success('User deleted');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const handleClearSubmissions = async (userId) => {
    if (!window.confirm('Clear all submissions for this user\'s forms?')) return;
    
    try {
      const result = await adminApi.clearSubmissions(userId);
      toast.success(result.message);
    } catch (error) {
      toast.error('Failed to clear submissions');
    }
  };

  const handleSaveCredentials = async () => {
    setSavingSettings(true);
    try {
      // Only send non-empty credentials
      const toSend = {};
      Object.entries(credentials).forEach(([key, value]) => {
        if (value && value.trim()) {
          toSend[key] = value.trim();
        }
      });
      
      if (Object.keys(toSend).length === 0) {
        toast.error('Please enter at least one credential');
        return;
      }
      
      await adminApi.updateIntegrations(toSend);
      toast.success('Credentials saved successfully');
      
      // Refresh integration status
      const newStatus = await adminApi.getIntegrationStatus();
      setIntegrationStatus(newStatus);
      
      // Re-fetch credentials to show saved values
      const newCredentials = await adminApi.getCredentials();
      if (newCredentials) {
        setCredentials({
          stripe_secret_key: newCredentials.stripe_secret_key || '',
          stripe_publishable_key: newCredentials.stripe_publishable_key || '',
          resend_api_key: newCredentials.resend_api_key || '',
          supabase_url: newCredentials.supabase_url || '',
          supabase_service_key: newCredentials.supabase_service_key || '',
          feedback_board_url: newCredentials.feedback_board_url || ''
        });
      }
    } catch (error) {
      toast.error('Failed to save credentials');
    } finally {
      setSavingSettings(false);
    }
  };

  const handleSaveCodeInjection = async () => {
    setSavingSettings(true);
    try {
      await adminApi.updateSettings({
        code_injection: codeInjection
      });
      toast.success('Code injection settings saved');
    } catch (error) {
      toast.error('Failed to save code injection settings');
    } finally {
      setSavingSettings(false);
    }
  };

  const handleSaveSeoSettings = async () => {
    setSavingSettings(true);
    try {
      await adminApi.updateSettings({
        seo: seoSettings
      });
      toast.success('SEO settings saved');
    } catch (error) {
      toast.error('Failed to save SEO settings');
    } finally {
      setSavingSettings(false);
    }
  };

  const handleSavePlans = async () => {
    setSavingSettings(true);
    try {
      await adminApi.updateSettings({
        plans: plans
      });
      toast.success('Plan settings saved');
    } catch (error) {
      toast.error('Failed to save plan settings');
    } finally {
      setSavingSettings(false);
    }
  };

  const handleSaveSettings = async () => {
    if (!platformSettings) return;
    
    setSavingSettings(true);
    try {
      await adminApi.updateSettings({
        site_name: platformSettings.site_name,
        support_email: platformSettings.support_email,
        maintenance_mode: platformSettings.maintenance_mode,
        allow_signups: platformSettings.allow_signups
      });
      toast.success('Settings saved');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSavingSettings(false);
    }
  };

  const filteredUsers = users.filter(u =>
    u.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    u.name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50">
        <FFLoader size={48} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="admin-page">
      {/* Header */}
      <header className="bg-zinc-900 text-white">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link 
              to="/dashboard" 
              className="p-2 hover:bg-zinc-800 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-[#FF6B6B]" />
              <h1 className="font-semibold">Admin Dashboard</h1>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/admin/audit-log" data-testid="admin-audit-log-link">
              <Button variant="ghost" size="sm" className="text-white hover:bg-zinc-800">
                <Shield className="w-4 h-4 mr-2" />
                Audit log
              </Button>
            </Link>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={fetchData}
              className="text-white hover:bg-zinc-800"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        <Tabs defaultValue="overview" className="space-y-6">
          <TabsList>
            <TabsTrigger value="overview">
              <BarChart3 className="w-4 h-4 mr-2" />
              Overview
            </TabsTrigger>
            <TabsTrigger value="traffic-revenue" data-testid="traffic-revenue-tab">
              <TrendingUp className="w-4 h-4 mr-2" />
              Traffic & Revenue
            </TabsTrigger>
            <TabsTrigger value="users">
              <Users className="w-4 h-4 mr-2" />
              Users
            </TabsTrigger>
            <TabsTrigger value="credentials" data-testid="credentials-tab">
              <Key className="w-4 h-4 mr-2" />
              Credentials
            </TabsTrigger>
            <TabsTrigger value="code-injection">
              <Settings className="w-4 h-4 mr-2" />
              Code Injection
            </TabsTrigger>
            <TabsTrigger value="blog" data-testid="blog-tab">
              <Newspaper className="w-4 h-4 mr-2" />
              Blog
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-zinc-500">Total Users</p>
                      <p className="text-3xl font-bold">{stats?.users?.total || 0}</p>
                    </div>
                    <Users className="w-10 h-10 text-zinc-300" />
                  </div>
                  <div className="mt-2 text-xs text-zinc-500">
                    <span className="text-green-600">{stats?.users?.active || 0} active</span>
                    {' · '}
                    <span className="text-red-600">{stats?.users?.banned || 0} banned</span>
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-zinc-500">Paid Users</p>
                      <p className="text-3xl font-bold">{stats?.users?.paid || 0}</p>
                    </div>
                    <CreditCard className="w-10 h-10 text-zinc-300" />
                  </div>
                  <div className="mt-2 text-xs text-zinc-500">
                    ${(stats?.users?.paid || 0) * 99} revenue
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-zinc-500">Total Forms</p>
                      <p className="text-3xl font-bold">{stats?.forms?.total || 0}</p>
                    </div>
                    <FileText className="w-10 h-10 text-zinc-300" />
                  </div>
                  <div className="mt-2 text-xs text-zinc-500">
                    {stats?.forms?.published || 0} published
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-zinc-500">Submissions</p>
                      <p className="text-3xl font-bold">{stats?.submissions?.total || 0}</p>
                    </div>
                    <BarChart3 className="w-10 h-10 text-zinc-300" />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* SEO utilities — one-off admin actions that keep our
                indexed presence fresh after each deploy. Kept in a
                separate card so the KPI row above stays clean. */}
            <Card className="mt-4" data-testid="admin-seo-utilities">
              <CardHeader>
                <CardTitle className="text-base">SEO utilities</CardTitle>
                <CardDescription>
                  Push every URL in the sitemap to Bing IndexNow. Search engines re-crawl within
                  hours instead of days, so redeploys that change titles or meta descriptions ship
                  to search results faster. Yandex, Naver and Seznam receive the same submission.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button
                  onClick={pushSitemapToIndexNow}
                  disabled={indexnowBusy}
                  data-testid="admin-indexnow-submit-btn"
                  className="bg-zinc-900 hover:bg-zinc-800 text-white"
                >
                  {indexnowBusy ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting...</>
                  ) : (
                    <><Globe className="w-4 h-4 mr-2" /> Push all URLs to Bing (IndexNow)</>
                  )}
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Traffic & Revenue Tab */}
          <TabsContent value="traffic-revenue">
            <TrafficRevenuePanel />
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Users</CardTitle>
                  <div className="relative w-64">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                    <Input
                      placeholder="Search users..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-9"
                      data-testid="admin-search-input"
                    />
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>User</TableHead>
                        <TableHead>Plan</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Forms</TableHead>
                        <TableHead>Last Login</TableHead>
                        <TableHead>Device / IP</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredUsers.map((u) => (
                        <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                          <TableCell>
                            <div>
                              <p className="font-medium">{u.name}</p>
                              <p className="text-xs text-zinc-500">{u.email}</p>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge variant={u.plan === 'paid' ? 'default' : 'secondary'}>
                              {u.plan}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Badge 
                              variant={u.status === 'active' ? 'default' : 'destructive'}
                              className={u.status === 'active' ? 'bg-green-100 text-green-800' : ''}
                            >
                              {u.status}
                            </Badge>
                          </TableCell>
                          <TableCell>{u.form_count || 0}</TableCell>
                          <TableCell className="text-zinc-500 text-sm">
                            {u.last_login ? formatDateTime(u.last_login) : 'Never'}
                          </TableCell>
                          <TableCell className="text-zinc-500 text-sm">
                            <div>
                              <p className="capitalize">{u.last_device || '-'}</p>
                              <p className="text-xs">{u.last_ip || '-'}</p>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => handleViewUser(u.id)}
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                              {u.role !== 'admin' && (
                                <>
                                  {u.status === 'banned' ? (
                                    <Button 
                                      variant="ghost" 
                                      size="sm"
                                      onClick={() => handleUnbanUser(u.id)}
                                    >
                                      <UserX className="w-4 h-4 text-green-600" />
                                    </Button>
                                  ) : (
                                    <Button 
                                      variant="ghost" 
                                      size="sm"
                                      onClick={() => handleBanUser(u.id)}
                                    >
                                      <Ban className="w-4 h-4 text-yellow-600" />
                                    </Button>
                                  )}
                                  <Button 
                                    variant="ghost" 
                                    size="sm"
                                    onClick={() => handleDeleteUser(u.id)}
                                  >
                                    <Trash2 className="w-4 h-4 text-red-600" />
                                  </Button>
                                </>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Credentials Tab */}
          <TabsContent value="credentials">
            <div className="grid gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Key className="w-5 h-5" />
                    Integration Credentials
                  </CardTitle>
                  <CardDescription>
                    Enter API keys and credentials for third-party services. 
                    These are securely stored and used for integrations.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Stripe */}
                  <div className="p-4 border rounded-lg space-y-4">
                    <div className="flex items-center gap-2">
                      <CreditCard className="w-5 h-5 text-[#635BFF]" />
                      <h3 className="font-semibold">Stripe (Payments)</h3>
                      {integrationStatus?.stripe?.configured && (
                        <Badge className="bg-green-100 text-green-800">Configured</Badge>
                      )}
                    </div>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="stripe_secret">Secret Key</Label>
                        <Input
                          id="stripe_secret"
                          type="password"
                          placeholder="sk_live_..."
                          value={credentials.stripe_secret_key}
                          onChange={(e) => setCredentials(prev => ({ ...prev, stripe_secret_key: e.target.value }))}
                          data-testid="stripe-secret-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="stripe_publishable">Publishable Key</Label>
                        <Input
                          id="stripe_publishable"
                          type="text"
                          placeholder="pk_live_..."
                          value={credentials.stripe_publishable_key}
                          onChange={(e) => setCredentials(prev => ({ ...prev, stripe_publishable_key: e.target.value }))}
                          data-testid="stripe-publishable-input"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Resend */}
                  <div className="p-4 border rounded-lg space-y-4">
                    <div className="flex items-center gap-2">
                      <Mail className="w-5 h-5 text-blue-600" />
                      <h3 className="font-semibold">Resend (Transactional Email)</h3>
                      {integrationStatus?.resend?.configured && (
                        <Badge className="bg-green-100 text-green-800">Configured</Badge>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="resend_key">API Key</Label>
                      <Input
                        id="resend_key"
                        type="password"
                        placeholder="re_..."
                        value={credentials.resend_api_key}
                        onChange={(e) => setCredentials(prev => ({ ...prev, resend_api_key: e.target.value }))}
                        data-testid="resend-key-input"
                      />
                    </div>
                  </div>

                  {/* Supabase */}
                  <div className="p-4 border rounded-lg space-y-4">
                    <div className="flex items-center gap-2">
                      <Database className="w-5 h-5 text-emerald-600" />
                      <h3 className="font-semibold">Supabase (Database, Auth & Storage)</h3>
                      {integrationStatus?.supabase?.configured && (
                        <Badge className="bg-green-100 text-green-800">Configured</Badge>
                      )}
                    </div>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="supabase_url">Project URL</Label>
                        <Input
                          id="supabase_url"
                          type="text"
                          placeholder="https://xxx.supabase.co"
                          value={credentials.supabase_url || ''}
                          onChange={(e) => setCredentials(prev => ({ ...prev, supabase_url: e.target.value }))}
                          data-testid="supabase-url-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="supabase_anon_key">Anon Key</Label>
                        <Input
                          id="supabase_anon_key"
                          type="password"
                          placeholder="eyJ..."
                          value={credentials.supabase_anon_key || ''}
                          onChange={(e) => setCredentials(prev => ({ ...prev, supabase_anon_key: e.target.value }))}
                          data-testid="supabase-anon-key-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="supabase_publishable_key">Publishable Key</Label>
                        <Input
                          id="supabase_publishable_key"
                          type="text"
                          placeholder="sb_publishable_..."
                          value={credentials.supabase_publishable_key || ''}
                          onChange={(e) => setCredentials(prev => ({ ...prev, supabase_publishable_key: e.target.value }))}
                          data-testid="supabase-publishable-key-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="supabase_secret_key">Secret Key</Label>
                        <Input
                          id="supabase_secret_key"
                          type="password"
                          placeholder="sb_secret_..."
                          value={credentials.supabase_secret_key || ''}
                          onChange={(e) => setCredentials(prev => ({ ...prev, supabase_secret_key: e.target.value }))}
                          data-testid="supabase-secret-key-input"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Feedback Board */}
                  <div className="p-4 border rounded-lg space-y-4">
                    <div className="flex items-center gap-2">
                      <MessageSquare className="w-5 h-5 text-blue-600" />
                      <h3 className="font-semibold">Feedback Board</h3>
                    </div>
                    <p className="text-sm text-zinc-500">
                      Link to an external feedback board (e.g., Canny, Featurebase). This will be used for the "Feedback" menu link.
                    </p>
                    <div className="space-y-2">
                      <Label htmlFor="feedback_url">Feedback Board URL</Label>
                      <Input
                        id="feedback_url"
                        type="url"
                        placeholder="https://yourcompany.canny.io or https://yourcompany.featurebase.app"
                        value={credentials.feedback_board_url || ''}
                        onChange={(e) => setCredentials(prev => ({ ...prev, feedback_board_url: e.target.value }))}
                        data-testid="feedback-url-input"
                      />
                    </div>
                  </div>

                  <Button 
                    onClick={handleSaveCredentials} 
                    disabled={savingSettings}
                    className="w-full"
                    data-testid="save-credentials-btn"
                  >
                    {savingSettings ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Save className="w-4 h-4 mr-2" />
                    )}
                    Save Credentials
                  </Button>

                  {/* Sentry Test */}
                  <div className="pt-6 border-t mt-6">
                    <h4 className="font-medium mb-2">Error Tracking Test</h4>
                    <p className="text-sm text-zinc-500 mb-3">
                      Click below to send a test error to Sentry and verify error tracking is working.
                    </p>
                    <SentryTestButton />
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Code Injection Tab */}
          <TabsContent value="code-injection">
            <Card>
              <CardHeader>
                <CardTitle>Code Injection</CardTitle>
                <CardDescription>Add custom code to all forms and pages</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-2">
                  <Label>Header Code (before &lt;/head&gt;)</Label>
                  <textarea 
                    className="w-full h-40 font-mono text-sm p-3 border rounded-lg"
                    placeholder="<!-- Add your header code here -->&#10;<!-- Google Analytics, Meta Pixel, etc. -->"
                    value={codeInjection.head_code}
                    onChange={(e) => setCodeInjection(prev => ({ ...prev, head_code: e.target.value }))}
                  />
                  <p className="text-xs text-zinc-500">This code will be added to the &lt;head&gt; section of all pages</p>
                </div>
                <div className="space-y-2">
                  <Label>Body Start Code (after &lt;body&gt;)</Label>
                  <textarea 
                    className="w-full h-40 font-mono text-sm p-3 border rounded-lg"
                    placeholder="<!-- Add your body start code here -->&#10;<!-- GTM noscript, etc. -->"
                    value={codeInjection.body_code}
                    onChange={(e) => setCodeInjection(prev => ({ ...prev, body_code: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Footer Code (before &lt;/body&gt;)</Label>
                  <textarea 
                    className="w-full h-40 font-mono text-sm p-3 border rounded-lg"
                    placeholder="<!-- Add your footer code here -->&#10;<!-- Chat widgets, tracking scripts, etc. -->"
                    value={codeInjection.footer_code}
                    onChange={(e) => setCodeInjection(prev => ({ ...prev, footer_code: e.target.value }))}
                  />
                </div>
                <Button onClick={handleSaveCodeInjection} disabled={savingSettings}>
                  {savingSettings ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4 mr-2" />
                  )}
                  Save Code Injection
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Blog Tab */}
          <TabsContent value="blog">
            <BlogManagementPanel />
          </TabsContent>


        </Tabs>
      </main>

      {/* User Details Modal */}
      <Dialog open={!!selectedUser} onOpenChange={() => setSelectedUser(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>User Details</DialogTitle>
          </DialogHeader>
          
          {userDetails && (
            <div className="space-y-6 py-4">
              {/* User Info */}
              <div className="grid grid-cols-2 gap-4 p-4 bg-zinc-50 rounded-lg">
                <div>
                  <p className="text-sm text-zinc-500">Name</p>
                  <p className="font-medium">{userDetails.user?.name}</p>
                </div>
                <div>
                  <p className="text-sm text-zinc-500">Email</p>
                  <p className="font-medium">{userDetails.user?.email}</p>
                </div>
                <div>
                  <p className="text-sm text-zinc-500">Plan</p>
                  <p className="font-medium capitalize">{userDetails.user?.plan}</p>
                </div>
                <div>
                  <p className="text-sm text-zinc-500">Status</p>
                  <p className="font-medium capitalize">{userDetails.user?.status}</p>
                </div>
                <div>
                  <p className="text-sm text-zinc-500">Joined</p>
                  <p className="font-medium">{formatDate(userDetails.user?.created_at)}</p>
                </div>
                <div>
                  <p className="text-sm text-zinc-500">Total Submissions</p>
                  <p className="font-medium">{userDetails.total_submissions}</p>
                </div>
              </div>

              {/* User Forms */}
              <div>
                <h4 className="font-semibold mb-3">Forms ({userDetails.forms?.length || 0})</h4>
                {userDetails.forms?.length > 0 ? (
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {userDetails.forms.map(form => (
                      <div key={form.id} className="flex items-center justify-between p-3 bg-zinc-50 rounded-lg">
                        <div>
                          <p className="font-medium">{form.title}</p>
                          <p className="text-xs text-zinc-500">
                            {form.submissions_count} submissions · {form.status}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-zinc-500 text-sm">No forms created</p>
                )}
              </div>

              {/* Actions */}
              <div className="flex gap-2 pt-4 border-t">
                <Button 
                  variant="outline"
                  onClick={() => handleClearSubmissions(selectedUser)}
                >
                  Clear Submissions
                </Button>
                {userDetails.user?.role !== 'admin' && (
                  <>
                    {userDetails.user?.status === 'banned' ? (
                      <Button onClick={() => handleUnbanUser(selectedUser)}>
                        Unban User
                      </Button>
                    ) : (
                      <Button 
                        variant="destructive"
                        onClick={() => handleBanUser(selectedUser)}
                      >
                        Ban User
                      </Button>
                    )}
                    <Button 
                      variant="destructive"
                      onClick={() => handleDeleteUser(selectedUser)}
                    >
                      Delete User
                    </Button>
                  </>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Admin;
